import {useEffect, useRef, useState} from 'react'
import {useRouteMatch} from 'react-router'
import {v4 as uuidv4} from 'uuid'
import {Config} from '../constants/Config'
import {ProductColor} from '../models/dto/common/productColor'
import {ProductPricing} from '../models/dto/common/productPricing'
import {DoubleProduct} from '../models/dto/getProduct.response'
import {ImgText} from '../models/editor/imgText'
import {Page} from '../models/editor/page'
import {useAppDispatch, useAppSelector} from '../stores/configureStore'
import {
  changeColor,
  flipSide,
  resetEditorStore,
  setPrintArea,
  setZoom,
} from '../stores/editor/editor.reducer'
import {
  resetToolboxStore,
  selectToolboxTabPanel,
} from '../stores/toolbox/toolbox.reducer'
import {
  getPrintAreaClientRect,
  getPrintAreaItemsClientRects,
} from '../utils/helpers/frontendEditor'
import {origo} from '../utils/helpers/origo'
import {calculatePrice} from '../utils/helpers/price'
import {usePrevious} from '../utils/helpers/usePrevious'
import services from '../utils/services'
import RenderImg from './editArea/Img'
//@ts-ignore
import cssMobile from '../css/editorMobile.module.css'
import {SavedEditor} from '../models/dto/common/savedEditor'

interface MatchParams {
  productIdString: string
  sharedId: string
}

declare global {
  interface Window {
    getPages: () => Array<number>
    selectPage: (index: number) => void
  }
}

export default function Preview() {
  const dispatch = useAppDispatch()
  const {images, printArea, maskImage, selectedImageId} = useAppSelector(
    store => store.editor,
  )
  const editorRef = useRef<HTMLDivElement | null>(null)
  const {productIdString, sharedId} = useRouteMatch<MatchParams>(
    '/preview/:productIdString/:sharedId',
  )?.params as MatchParams
  const productId = parseInt(productIdString)
  const [, setIsTwoSided] = useState(false)
  const [, setTextOnly] = useState(false)
  const [colors, setColors] = useState<ProductColor[]>([])
  const [selectedColor, setSelectedColor] = useState<ProductColor | undefined>()
  const [productPricing, setProductPricing] = useState<ProductPricing | null>(
    null,
  )

  const [pages, setPages] = useState<Page[]>([])
  const [selectedPageIndex, setSelectedPageIndex] = useState<
    number | undefined
  >(undefined)
  const previousSelectedPageIndex = usePrevious(selectedPageIndex)
  const [calendar, setCalendar] = useState(false)

  useEffect(() => {
    window.getPages = () => {
      return pages.map((_, index) => index)
    }
    window.selectPage = (index: number) => {
      setSelectedPageIndex(() => index)
    }
  }, [pages])

  useEffect(() => {
    const newColorProduct = colors.find(
      colorProduct => colorProduct.color === selectedColor?.color,
    )
    if (newColorProduct && selectedPageIndex !== undefined) {
      const newUrl = newColorProduct.pages[selectedPageIndex].imageUrl
      const newMaskUrl = newColorProduct.pages[selectedPageIndex].maskUrl
      setPages(pages =>
        pages.map((page, index) => ({
          ...page,
          images: page.images.map((image, i) =>
            i === 0
              ? {...image, url: newColorProduct.pages[index].imageUrl}
              : image,
          ),
          mask: newColorProduct.pages[index].maskUrl
            ? {
                id: uuidv4(),
                type: 'url',
                url: newColorProduct.pages[index].maskUrl as string,
                brightness: 1,
                filters: {inverse: false, monochrome: false, sepia: false},
                inert: true,
                locked: true,
                mirroredX: false,
                mirroredY: false,
                position: origo,
                rotation: 0,
                scale: 1,
              }
            : null,
        })),
      )
      dispatch(changeColor({url: newUrl, maskUrl: newMaskUrl}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedColor])

  useEffect(() => {
    if (
      previousSelectedPageIndex !== undefined &&
      productPricing &&
      selectedPageIndex !== undefined &&
      pages[selectedPageIndex].printArea
    ) {
      setPages(pages =>
        pages.map((page, index) => {
          return index === previousSelectedPageIndex
            ? {
                ...page,
                images: images,
                mask: maskImage,
                price:
                  (printArea &&
                    page.printAreaNativeWidth &&
                    page.printAreaNativeHeight &&
                    calculatePrice(
                      getPrintAreaClientRect(printArea),
                      page.printAreaNativeWidth,
                      page.printAreaNativeHeight,
                      getPrintAreaItemsClientRects(images),
                      productPricing,
                      selectedPageIndex,
                      calendar,
                      index === 1,
                      pages.length > 1 && pages[0]?.images?.length > 1,
                      previousSelectedPageIndex === 1,
                    )) ??
                  0,
                imageClientRects: getPrintAreaItemsClientRects(images),
              }
            : page
        }),
      )
      dispatch(
        flipSide({
          images: pages[selectedPageIndex].images,
          maskImage: pages[selectedPageIndex].mask,
        }),
      )
      dispatch(
        setPrintArea({
          printArea: pages[selectedPageIndex].printArea as ImgText,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedPageIndex])

  useEffect(() => {
    if (printArea && selectedColor) {
      const newPrintArea: ImgText = {
        ...printArea,
        text: printArea?.text.replace(
          /stroke:#(?:[0-9a-fA-F]{3}){1,2}/,
          `stroke:${selectedColor.lineColor ?? '#ffffff'}`,
        ),
      }
      dispatch(setPrintArea({printArea: newPrintArea}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedColor])

  useEffect(() => {
    const tryGetPic = async () => {
      const response = await services.getProduct(productId)
      const product = response.data
      setColors(product.colors)

      setIsTwoSided(product.twoSided)
      setTextOnly(product.textOnly)
      if (product.textOnly) {
        dispatch(selectToolboxTabPanel({tabPanel: 'AddCaption'}))
      }
      setProductPricing(product.pricing)
      setCalendar(product.calendar)

      let saved: SavedEditor | undefined
      let savedColorIndex = 0
      if (sharedId) {
        saved = (await services.getStoredState(sharedId)).data
        savedColorIndex = product.colors.findIndex(
          item => item.color === saved?.color?.color,
        )
        if (savedColorIndex === -1) {
          savedColorIndex = 0
        }
      }

      let tempPages = product.colors[savedColorIndex].pages.map((p, index) => {
        let printWidthMm = product.pricing.szivoldalWidthMm
        let printHeightMm = product.pricing.szivoldalHeightMm
        if (product.pricing.editableArea === 'a4') {
          printWidthMm = product.pricing.a4WidthMm
          printHeightMm = product.pricing.a4HeightMm
        } else if (product.pricing.editableArea === 'a3') {
          printWidthMm = product.pricing.a3WidthMm
          printHeightMm = product.pricing.a3HeightMm
        }
        if (product.twoSided && index === 1) {
          printWidthMm = product.pricing.szivoldalWidthMm
          printHeightMm = product.pricing.szivoldalHeightMm
          if (product.pricing.editableAreaBack === 'a4') {
            printWidthMm = product.pricing.a4WidthMm
            printHeightMm = product.pricing.a4HeightMm
          } else if (product.pricing.editableAreaBack === 'a3') {
            printWidthMm = product.pricing.a3WidthMm
            printHeightMm = product.pricing.a3HeightMm
          }
        }

        const page: Page = {
          images: [
            {
              id: uuidv4(),
              type: 'url',
              url: p.imageUrl,
              brightness: 1,
              filters: {inverse: false, monochrome: false, sepia: false},
              inert: true,
              locked: true,
              mirroredX: false,
              mirroredY: false,
              position: origo,
              rotation: 0,
              scale: 1,
            },
          ],
          mask: p.maskUrl
            ? {
                id: uuidv4(),
                type: 'url',
                url: p.maskUrl,
                brightness: 1,
                filters: {inverse: false, monochrome: false, sepia: false},
                inert: true,
                locked: true,
                mirroredX: false,
                mirroredY: false,
                position: origo,
                rotation: 0,
                scale: 1,
              }
            : null,
          price: 0,
          text: p.text,
          printArea: {
            id: uuidv4(),
            type: 'text',
            text: `<svg width="${printWidthMm}mm" height="${printHeightMm}mm" viewBox="0 0 ${printWidthMm}mm ${printHeightMm}mm">
                  <rect width="${printWidthMm}mm" height="${printHeightMm}mm" style="fill:transparent;stroke-width:4;stroke:${
              product.colors[savedColorIndex].lineColor ?? 'black'
            }" stroke-dasharray="5,5" vector-effect: non-scaling-stroke; />
                  </svg>`,
            brightness: 1,
            filters: {inverse: false, monochrome: false, sepia: false},
            inert: true,
            locked: true,
            mirroredX: false,
            mirroredY: false,
            position:
              product.twoSided && index === 1
                ? (product as DoubleProduct).sides.back.position
                : product.sides.front.position,
            rotation: 0,
            scale:
              product.twoSided && index === 1
                ? (product as DoubleProduct).sides.back.scale
                : product.sides.front.scale,
          },
          printAreaClientRect: {
            type: 'printArea',
            // Dummy data
            x1: 377.9864501953125,
            x2: 461.1357727050781,
            y1: 123.48182678222656,
            y2: 241.06631469726562,
          },
          printAreaNativeWidth: printWidthMm,
          printAreaNativeHeight: printHeightMm,
          imageClientRects: [],
        }
        return page
      })

      if (saved && saved.pages.length === tempPages.length) {
        const typesafeSaved: SavedEditor = saved
        tempPages = tempPages.map((page, index) => ({
          ...page,
          images: [...page.images, ...typesafeSaved.pages[index].images],
          price: typesafeSaved.pages[index].price,
          imageClientRects: typesafeSaved.pages[index].imageClientRects,
        }))
      }

      setPages(tempPages)
      setSelectedPageIndex(0)

      dispatch(resetEditorStore())
      dispatch(resetToolboxStore())
      let zoomLevel = 0.2
      const zoomedOutProducts = [
        12813, 2732, 3823, 12212, 7868, 7922, 7925, 7964, 7975, 7976, 7977,
        7978,
      ]
      if (zoomedOutProducts.find(id => id === productId) !== undefined) {
        zoomLevel = Config.minZoom
      }
      dispatch(setZoom({level: zoomLevel, translate: origo}))
      dispatch(
        flipSide({
          images: tempPages[0].images,
          maskImage: tempPages[0].mask,
        }),
      )
      if (tempPages[0].printArea) {
        dispatch(setPrintArea({printArea: tempPages[0].printArea}))
      }
      setSelectedColor(saved?.color ?? product.colors[0])
    }
    tryGetPic()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productId])

  const selectedImage = images.find(i => i.id === selectedImageId)

  if (
    selectedImage &&
    selectedImage.type === 'masked' &&
    selectedImage.inside.length === 1 &&
    selectedImage.inside[0].url ===
      `${Config.websiteUrl}/wp-content/themes/poloforma/assets/mask:placeholder.png`
  ) {
    dispatch(
      selectToolboxTabPanel({
        tabPanel: 'PhotoUpload',
        selectForMasked: {id: selectedImage.id, insideIndex: 0},
      }),
    )
  }

  return printArea ? (
    <div className={cssMobile.editor}>
      <div className={`${cssMobile.editorLayer}`} ref={editorRef}>
        <div className={cssMobile.editArea}>
          {images.map(image => (
            <RenderImg img={image} key={`editorImageList_${image.id}`} />
          ))}
          {maskImage && (
            <RenderImg
              className={selectedImageId ? 'maskOpaque' : undefined}
              img={maskImage}
              key={`editorMask_${maskImage.id}`}
              enableMaskFrame
            />
          )}
          {printArea && (
            <RenderImg
              img={printArea}
              key={`editorPrintArea_${printArea.id}`}
              className="print-hidden"
            />
          )}
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        html {
          height: 100%;
        }
        body {
          margin: 0;
          height: 100%;
          touch-action: none;
        }
        #root {
          height: 100%;
        }`,
        }}></style>
    </div>
  ) : null
}
